import React, { Suspense, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { resolveSuspenseFallback } from "./react_suspense_fallback_registry";

// eslint-disable-next-line react/prefer-es6-class
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("React component error:", error, info);
  }

  render() {
    if (this.state.hasError) {
      // eslint-disable-next-line react/prop-types
      return this.props.fallback;
    }
    // eslint-disable-next-line react/prop-types
    return this.props.children;
  }
}

const ErrorComponent = ({ error }) => (
  <div
    style={{
      color: "red",
    }}
  >
    {error}
  </div>
);

const SkeletonRemover = ({ children }) => {
  const ref = useRef(null);
  useEffect(() => {
    const skeleton = ref.current?.closest("[data-react-class]")?.parentElement?.querySelector(".js-skeleton");
    if (skeleton) skeleton.remove();
  }, []);
  return <div ref={ref}>{children}</div>;
};

const StableComponent = React.memo(({ Component, props, suspenseFallback, hasSkeleton }) => (
  <ErrorBoundary fallback={<ErrorComponent error="Something went wrong." />}>
    <Suspense fallback={suspenseFallback}>
      {hasSkeleton ? (
        <SkeletonRemover>
          <Component {...props} />
        </SkeletonRemover>
      ) : (
        <Component {...props} />
      )}
    </Suspense>
  </ErrorBoundary>
));

export const ReactRailsUJS = {
  CLASS_NAME_ATTR: "data-react-class",
  PROPS_ATTR: "data-react-props",
  CACHE_ID_ATTR: "data-react-cache-id",
  TURBOLINKS_PERMANENT_ATTR: "data-turbolinks-permanent",
  SUSPENSE_FALLBACK_ATTR: "data-react-suspense-fallback",
  jQuery:
    typeof window !== "undefined" &&
    typeof window.jQuery !== "undefined" &&
    window.jQuery,

  components: {},
  roots: [],
  lazyComponents: new Map(),

  getLazyComponent: (className) => {
    if (!ReactRailsUJS.lazyComponents.has(className)) {
      const lazyComponent = React.lazy(() =>
        import(`../components/${className.replace("/index", "")}/index`)
          .then((module) => {
            return {
              default: (props) => {
                const Component = module.default;
                return <Component {...props} />;
              },
            };
          })
          .catch((error) => {
            console.error(
              `Error mounting React component '${className}':`,
              error,
            );

            return {
              default: () => (
                <ErrorComponent
                  error={`Failed to load ${className}: ${error.message}`}
                />
              ),
            };
          }),
      );

      ReactRailsUJS.lazyComponents.set(className, lazyComponent);
    }

    return ReactRailsUJS.lazyComponents.get(className);
  },

  findDOMNodes: (searchSelector) => {
    var classNameAttr = ReactRailsUJS.CLASS_NAME_ATTR;
    var selector, parent;

    switch (typeof searchSelector) {
      case "undefined":
        selector = "[" + classNameAttr + "]";
        parent = document;
        break;
      case "object":
        selector = "[" + classNameAttr + "]";
        parent = searchSelector;
        break;
      case "string":
        selector =
          searchSelector +
          "[" +
          classNameAttr +
          "], " +
          searchSelector +
          " [" +
          classNameAttr +
          "]";
        parent = document;
        break;
      default:
        break;
    }

    if (ReactRailsUJS.jQuery) {
      return ReactRailsUJS.jQuery(selector, parent);
    } else {
      return parent.querySelectorAll(selector);
    }
  },

  getSuspenseFallback: (node) => {
    const fallbackType = node.getAttribute(
      ReactRailsUJS.SUSPENSE_FALLBACK_ATTR,
    );

    return resolveSuspenseFallback(fallbackType);
  },

  findOrCreateRoot: function (node) {
    const existingRoot = this.roots.find((r) => r.node === node);

    if (existingRoot) {
      return existingRoot.root;
    }

    const root = createRoot(node);
    this.roots.push({ node, root });
    return root;
  },

  unmountRoot: function (node) {
    const rootIndex = this.roots.findIndex((r) => r.node === node);

    if (rootIndex !== -1) {
      const { root } = this.roots[rootIndex];

      root.unmount();

      this.roots.splice(rootIndex, 1);
    }
  },

  mountComponents: (searchSelector) => {
    var ujs = ReactRailsUJS;
    var nodes = ujs.findDOMNodes(searchSelector);

    Array.from(nodes).forEach((node) => {
      const className = node.getAttribute(ujs.CLASS_NAME_ATTR);
      const propsJson = node.getAttribute(ujs.PROPS_ATTR);
      const props = propsJson && JSON.parse(propsJson);
      const suspenseFallback = ujs.getSuspenseFallback(node);

      const LazyComponent = ujs.getLazyComponent(className);
      const root = ujs.findOrCreateRoot(node);
      const hasSkeleton = !!node.parentElement?.querySelector(".js-skeleton");

      root.render(
        <React.StrictMode>
          <StableComponent
            Component={LazyComponent}
            props={props}
            suspenseFallback={suspenseFallback}
            hasSkeleton={hasSkeleton}
          />
        </React.StrictMode>,
      );
    });
  },

  unmountComponents: (searchSelector) => {
    var ujs = ReactRailsUJS;
    var nodes = ReactRailsUJS.findDOMNodes(searchSelector);

    for (var i = 0; i < nodes.length; ++i) {
      var node = nodes[i];
      ujs.unmountRoot(node);
    }
  },

  detectEvents: () => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", ReactRailsUJS.handleMount);
    } else {
      ReactRailsUJS.mountComponents();
    }

    window.addEventListener("turbolinks:load", ReactRailsUJS.handleMount);
    window.addEventListener(
      "turbolinks:before-cache",
      ReactRailsUJS.handleUnmount,
    );
  },

  handleMount: (e) => {
    var target = e && e.target ? e.target : undefined;
    ReactRailsUJS.mountComponents(target);
  },

  handleUnmount: (e) => {
    var target = e && e.target ? e.target : undefined;
    ReactRailsUJS.unmountComponents(target);
  },
};

if (typeof window !== "undefined") {
  window.ReactRailsUJS = ReactRailsUJS;
}

ReactRailsUJS.detectEvents();

export default ReactRailsUJS;
